/* Built In Imports */
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import Breadcrum from '@components/Navigation/Breadcrum';
import LeftNavCommonUI from '@components/UI/Common/LeftNavCommonUI';
import OthersCommonUI from '@components/UI/Others/OthersCommonUI';
import HeadComponent from '@components/Utility/HeadComponent';
import config from '@config';
import WrapLayout from '@Layout/WrapLayout';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import { useRouter } from 'next/router';
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/othersService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Center Landing Page Data
 */

const OtherSections = ({ region, language, pageData }) => {
  const router = useRouter();
  const path = router.asPath;
  const isMom = path.indexOf('miracle-of-mind') > -1;
  let isDeathOfBook = path.indexOf('death-book') > -1;
  let isGMYouthPage = path.indexOf('lap-of-master') > -1;
  //console.log('pageData', pageData);
  const pageConfig = pageData?.pageConfig != null ? pageData?.pageConfig : '';
  const breadcrumbVisibility =
    pageData?.pageConfig != null
      ? pageData?.pageConfig[0]?.value?.breadcrumb !== 'disable'
      : '';
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  return (
    <WrapLayout>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
        pageconfig={pageData?.pageConfig}
      >
        {' '}
      </HeadComponent>
      {breadcrumbVisibility && (
        <Breadcrum
          page={pageData?.breadcrumbs}
          alsoin={pageData?.alsoin}
          url=""
          region={region}
          lang={language}
          pageConfig={pageData?.pageConfig}
        />
      )}

      <Box
        overflowX={isDeathOfBook ? 'hidden' : undefined}
        as="section"
        className="page-wrap container-article"
        p="0"
        maxW={(isMom || isDeathOfBook || isGMYouthPage) && '1920px !important'}
        bgColor={isGMYouthPage ? '#F1EBDF' : ''}
      >
        <OthersCommonUI
          // pageBody={pageData.body}
          pageBody={
            pageData?.body?.[0]?.value?.length
              ? pageData?.body[0]?.value
              : pageData?.body
          }
          region={region}
          lang={language}
          pageName={pageConfig?.pageName}
          pageConfig={pageConfig}
        />
        <LeftNavCommonUI
          pageBody={pageData?.bodyLeftNavSections}
          region={region}
          lang={language}
          isLeftMenu
        />
        <OthersCommonUI
          pageBody={pageData?.bodyBottomSection}
          region={region}
          lang={language}
        />
      </Box>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const othersLandingPaths = config.USE_LOCAL_URL
    ? [
        `/in/en/iso-component-library`,
        `/in/ta/guru-purnima`,
        `/in/en/stay-in-touch-with-sadhguru-and-isha`,
        `/in/en/isha-covid-action`,
        `/in/en/inner-engineering-book`,
        `/in/ta/maranambook`,
        `/us/en/inner-engineering-book`,
        `/in/en/about-isha-foundation`,
        `/in/ta/isha-kaattupoo-magazine`,
        `/in/en/sanghamitra`,
        `/in/en/science-spirituality`,
        `/in/en/guru-purnima`,
        `/in/en/miracle-of-mind-old`,
        `/in/en/iso-component-library`,
        `/in/en/iso-generic-component`,
        `/in/en/death-book`,
      ]
    : await getLandingPaths('lvl0', '/', [
        '/sadhguru',
        '/blog',
        '/center',
        '/events',
        '/donation',
        '/donation-new',
        '/health',
        '/inner-engineering',
        '/inner-engineering-pc',
        '/inner-engineering-pc-2',
        '/inner-engineering-migration/',
        '/inner-engineering-online',
        '/maha-yoga-yagna',
        '/grace-of-yoga',
        '/my-account',
        '/program-details',
        '/program-finder',
        '/program-embed-register',
        '/quotes',
        '/rudraksha-diksha',
        '/sadhguru-exclusive',
        '/sadhguru-exclusive-free-trial',
        '/subscribe-confirmation',
        '/subscribe-failure',
        '/topic',
        '/volunteer',
        '/wisdom',
        '/yoga-meditation',
        '/redirect-to-site',
        '/program-finder-us',
        '/news',
        '/outreach',
        '/search',
        '/youth-n-truth',
        '/isha-foundation',
        '/sadhguru-radio',
        '/sadhguru-app',
        '/sadhguru-school-uganda',
        '/project-samskriti',
        '/mahashivratri',
        '/sadhguru-academy',
        '/sacred-walks',
        '/linga-bhairavi',
        '/daily-quotes-subscription',
        '/publications',
        '/sadhguru-gurukulam',
        '/sadhguru-schedule',
        '/instagram-link-bio',
        '/pancha-bhuta-kriya-online-with-sadhguru-mahashivratri',
        '/pancha-bhuta-kriya',
        `pancha-bhuta-kriya-old`,
        '/official-digital-handles',
        'corporate-social-responsibility',
        '/21-day-challenge-idy',
        '/refer-21-day-challenge-idy',
        '/kshetra-sankalpa-sadhguru-sannidhi',
        `/career`,
        '/job-listings',
        '/miracle-of-mind',
        '/register-tr',
      ]);
  return {
    paths: othersLandingPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language, others },
  req,
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  const pageData = await fetchDetails(region, language, `${others}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      headerVisible: others === 'miracle-of-mind' ? false : true,
      footerData:
        others === 'miracle-of-mind' ? false : footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
    },
  };
};

export default OtherSections;
